/**
 * ConflictResolutionDialog - Modal dialog for handling file/folder name conflicts
 * 
 * Displays when a file or folder with the same name exists in the destination folder
 * during drag-and-drop or move operations.
 * 
 * Offers three options:
 * - Replace: Overwrite the existing file/folder
 * - Rename: Keep both by adding a suffix to the new file/folder
 * - Skip: Cancel the move for this item
 * 
 * Requirements: 5.4
 * - IF a file with the same name exists in the destination folder, 
 *   THEN THE FileExplorer SHALL prompt the user to replace, rename, or cancel
 * 
 * @module ConflictResolutionDialog
 */

import React, { useState } from 'react';
import './ConflictResolutionDialog.css';

/**
 * ConflictResolutionDialog component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the dialog is visible
 * @param {string} props.itemName - Name of the conflicting item
 * @param {string} props.itemType - Type of item ('file' or 'folder')
 * @param {string} props.destinationFolder - Name of the destination folder
 * @param {function} props.onReplace - Callback when user chooses to replace
 * @param {function} props.onRename - Callback when user chooses to rename (receives new name)
 * @param {function} props.onSkip - Callback when user chooses to skip/cancel
 * @param {function} props.onClose - Callback to close the dialog
 */
const ConflictResolutionDialog = ({
  isOpen,
  itemName,
  itemType = 'file',
  destinationFolder,
  onReplace,
  onRename,
  onSkip,
  onClose
}) => {
  const [showRenameInput, setShowRenameInput] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  // Generate a suggested new name by adding a suffix
  const generateSuggestedName = (name) => {
    const lastDotIndex = name.lastIndexOf('.');
    if (lastDotIndex > 0 && itemType === 'file') {
      // For files with extension, insert suffix before extension
      const baseName = name.substring(0, lastDotIndex);
      const extension = name.substring(lastDotIndex);
      return `${baseName} (copy)${extension}`;
    }
    // For folders or files without extension
    return `${name} (copy)`;
  };

  // Handle showing the rename input
  const handleRenameClick = () => {
    setShowRenameInput(true);
    setNewName(generateSuggestedName(itemName));
    setError('');
  };

  // Handle rename submission
  const handleRenameSubmit = () => {
    if (!newName.trim()) {
      setError('Name cannot be empty');
      return;
    }
    
    if (newName.trim() === itemName) {
      setError('New name must be different from the original');
      return;
    }

    // Check for invalid characters
    const invalidChars = /[/\\:*?"<>|]/;
    if (invalidChars.test(newName)) {
      setError('Name contains invalid characters: / \\ : * ? " < > |');
      return;
    }

    onRename(newName.trim());
    resetState();
  };

  // Handle cancel/skip
  const handleSkip = () => {
    onSkip();
    resetState();
  };

  // Handle replace
  const handleReplace = () => {
    onReplace();
    resetState();
  };

  // Reset dialog state
  const resetState = () => {
    setShowRenameInput(false);
    setNewName('');
    setError('');
  };

  // Handle key press in rename input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setShowRenameInput(false);
      setError('');
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="conflict-dialog-overlay" data-testid="conflict-dialog">
      <div className="conflict-dialog">
        <div className="conflict-dialog-header">
          <h3>Name Conflict</h3>
        </div>
        
        <div className="conflict-dialog-content">
          <p className="conflict-message">
            A {itemType} named <strong>"{itemName}"</strong> already exists in{' '}
            <strong>"{destinationFolder || 'Root'}"</strong>.
          </p>
          
          <p className="conflict-question">
            What would you like to do?
          </p>

          {showRenameInput ? (
            <div className="rename-input-container">
              <label htmlFor="new-name-input">Enter a new name:</label>
              <input
                id="new-name-input"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={handleKeyPress}
                className={error ? 'input-error' : ''}
                autoFocus
                data-testid="rename-input"
              />
              {error && <span className="error-text">{error}</span>}
              <div className="rename-actions">
                <button 
                  onClick={handleRenameSubmit}
                  className="confirm-rename-button"
                  data-testid="confirm-rename-button"
                >
                  Confirm
                </button>
                <button 
                  onClick={() => setShowRenameInput(false)}
                  className="cancel-rename-button"
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="conflict-actions">
              <button 
                onClick={handleReplace}
                className="replace-button"
                data-testid="replace-button"
              >
                Replace
                <span className="button-description">Overwrite the existing {itemType}</span>
              </button>
              
              <button 
                onClick={handleRenameClick}
                className="rename-button"
                data-testid="rename-button"
              >
                Keep Both
                <span className="button-description">Rename the {itemType} being moved</span>
              </button>
              
              <button 
                onClick={handleSkip}
                className="skip-button"
                data-testid="skip-button"
              >
                Skip
                <span className="button-description">Don't move this {itemType}</span>
              </button>
            </div>
          )}
        </div>
        
        <div className="conflict-dialog-footer">
          <button 
            onClick={handleSkip}
            className="close-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionDialog;
